import {
  Box,
  Spinner,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { DatevLaudsLoginDocument, DatevLaudsTokenStatusFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { Status } from 'modules/payroll_integrations_datev_lauds/lib/OauthService'
import { DatevWizardData } from 'modules/payroll_integrations_datev_lauds/lib/types'

import { LogIn, NotAuthenticated } from './Authentication'
import { Header } from './Header'
import { Hints } from './Hints'
import { Logout } from './Logout'

const findCurrentToken = (
  statuses: DatevLaudsTokenStatusFragment[],
  companyId: number | null | undefined
) => {
  if (!companyId) {
    // When there is no configuration we don't have any companyId assigned.
    // In this case we return the first token.
    // It should be the unique token for the company as we don't allow creating more than a token.
    return statuses[0]
  }

  const tokenStatus = statuses.find((token) => token.company.id === companyId)

  return tokenStatus
}

const LoginStep = ({ data, onDataChanged }: WizardStepRenderParams<DatevWizardData>) => {
  const {
    access: { id: accessId },
    company: { id: companyId },
  } = useCurrentSession()
  const { data: stepData, loading } = useQuery(DatevLaudsLoginDocument, {
    variables: {
      companyId: companyId,
      accessId: accessId,
    },
  })

  const tokenStatuses = stepData?.integrationsDatevLauds.oauthTokensConnection.nodes || []
  const currentToken = findCurrentToken(tokenStatuses, null)
  const loginStatus = currentToken?.status
  const isAuthenticated = loginStatus === Status.Authenticated

  useEffect(() => {
    onDataChanged({
      ...data,
      answers: {
        ...data.answers,
        authenticated_access_id: isAuthenticated ? String(accessId) : '',
      },
    })
  }, [loading, isAuthenticated])

  useOnNext(() => {
    track(trackingEvents.payrollIntegrationsDatevLauds.datevLaudsSettingsLoginSuccessfully, {
      company_id: companyId,
      access_id: accessId,
    })
  })

  return loading ? (
    <Spinner />
  ) : (
    <>
      <Box gap="s24">
        <Header isLoggedIn={!!currentToken && isAuthenticated} />
        <Box justifyContent="spaceBetween" flexDirection="column" gap="s24">
          {!currentToken || !isAuthenticated ? (
            <LogIn companyId={companyId} accessId={accessId} />
          ) : (
            <Box maxWidth="s600" flexDirection="column" gap="s24">
              {currentToken && (
                <Logout
                  owner={
                    currentToken?.tokenUserName ||
                    currentToken?.tokenUserEmail ||
                    currentToken?.access?.fullName
                  }
                  createdAt={currentToken.createdAt}
                  companyId={companyId}
                  accessId={accessId}
                />
              )}

              {!isAuthenticated && <NotAuthenticated companyId={companyId} accessId={accessId} />}
            </Box>
          )}
        </Box>
      </Box>
      {!isAuthenticated && <Hints />}
    </>
  )
}

export const loginStep = (): WizardStep<DatevWizardData> => ({
  title: i18n.t(
    'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.login.wizard_title'
  ),
  nextText: i18n.t('common.next'),
  render: (props) => <LoginStep {...props} />,
  isCompleted: (data) => !!data.answers.authenticated_access_id,
})
